import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Fab,
  InputAdornment,
  Alert,
  Stack,
  Divider,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Receipt as InvoiceIcon,
  LocalShipping as DeliveryIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Inventory as InventoryIcon,
  Assignment as DraftIcon,
  HourglassEmpty as ProcessingIcon,
  ViewModule as CardViewIcon,
  ViewList as ListViewIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import InvoiceConversionDialog from '../../components/documents/InvoiceConversionDialog'
import { salesOrderService } from '../../services/salesOrderService'
import { SalesOrder } from '../../types/salesOrder'
import { formatCurrency } from '../../utils/formatters'
import { useAuthStore } from '../../stores/authStore'

const SalesOrdersPage: React.FC = () => {
  const { hasPermission } = useAuthStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedOrder, setSelectedOrder] = useState<SalesOrder | null>(null)
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(12)
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card')
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  // Fetch sales orders from API
  const { data, isLoading, error } = useQuery({
    queryKey: ['salesOrders', page, pageSize, searchTerm],
    queryFn: () => salesOrderService.getSalesOrders({
      page,
      per_page: pageSize,
      search_term: searchTerm || undefined
    })
  })

  const getStatusConfig = (status: string) => {
    const statusMap: Record<string, { color: any, icon: JSX.Element, label: string }> = {
      draft: { color: 'default', icon: <DraftIcon fontSize="small" />, label: 'Draft' },
      confirmed: { color: 'info', icon: <CheckCircleIcon fontSize="small" />, label: 'Confirmed' },
      processing: { color: 'warning', icon: <ProcessingIcon fontSize="small" />, label: 'Processing' },
      ready_to_ship: { color: 'info', icon: <InventoryIcon fontSize="small" />, label: 'Ready to Ship' },
      shipped: { color: 'primary', icon: <DeliveryIcon fontSize="small" />, label: 'Shipped' },
      delivered: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Delivered' },
      invoiced: { color: 'success', icon: <InvoiceIcon fontSize="small" />, label: 'Invoiced' },
      cancelled: { color: 'error', icon: <ScheduleIcon fontSize="small" />, label: 'Cancelled' },
    }
    return statusMap[status] || { color: 'default', icon: <ScheduleIcon fontSize="small" />, label: status }
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, order: SalesOrder) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedOrder(order)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedOrder(null)
  }

  const handleConvertToInvoice = () => {
    handleMenuClose()
    setInvoiceDialogOpen(true)
  }

  const handleInvoiceConversionSuccess = (invoiceId: string, invoiceNumber: string) => {
    enqueueSnackbar(`Invoice ${invoiceNumber} created successfully!`, { variant: 'success' })
    setInvoiceDialogOpen(false)
    setSelectedOrder(null)
    navigate(`/admin/documents/invoices/${invoiceId}`)
  }

  const handleCardClick = (orderId: string) => {
    navigate(`/admin/documents/sales-orders/${orderId}`)
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load sales orders. Please try again later.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Sales Orders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.total || 0} total orders
          </Typography>
        </Box>
        {hasPermission('orders.create') && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/documents/sales-orders/create')}
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
          >
            Create Order
          </Button>
        )}
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search by order number, customer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && setViewMode(newMode)}
                size="small"
              >
                <ToggleButton value="card">
                  <CardViewIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ListViewIcon />
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Sales Orders Grid/List */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography>Loading sales orders...</Typography>
        </Box>
      ) : viewMode === 'card' ? (
        <>
          <Grid container spacing={2}>
            {data?.sales_orders?.map((order) => {
              const statusConfig = getStatusConfig(order.status)
              const isInvoiced = !!order.converted_to_invoice_id

              return (
                <Grid item xs={12} sm={6} md={4} key={order.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover': {
                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => handleCardClick(order.id)}
                  >
                    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                      {/* Header with Order Number and Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight={600} color="primary" gutterBottom>
                            {order.order_number}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            <Chip
                              icon={statusConfig.icon}
                              label={statusConfig.label}
                              color={statusConfig.color}
                              size="small"
                              sx={{ borderRadius: 4 }}
                            />
                            {isInvoiced && (
                              <Chip
                                icon={<InvoiceIcon fontSize="small" />}
                                label="Invoiced"
                                color="success"
                                size="small"
                                variant="outlined"
                                sx={{ borderRadius: 4 }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, order)}
                          sx={{ mt: -1, mr: -1 }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Customer Info */}
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                              Customer
                            </Typography>
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {order.customer?.billing_name || order.customer?.name || order.customer?.contact_person || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Source Estimate */}
                        {order.estimate && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                From Estimate
                              </Typography>
                              <Typography variant="body2" fontWeight={500} color="info.main" noWrap>
                                {order.estimate.estimate_number}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Dates */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                Order Date
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={500}>
                              {format(new Date(order.order_date), 'dd/MM/yyyy')}
                            </Typography>
                          </Box>
                          {order.expected_delivery_date && (
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <DeliveryIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  Delivery
                                </Typography>
                              </Box>
                              <Typography variant="body2" fontWeight={500}>
                                {format(new Date(order.expected_delivery_date), 'dd/MM/yyyy')}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Amount */}
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <MoneyIcon sx={{ fontSize: 14, color: 'success.main' }} />
                            <Typography variant="caption" color="text.secondary">
                              Total Amount
                            </Typography>
                          </Box>
                          <Typography variant="h6" fontWeight={600} color="success.main">
                            {formatCurrency(order.total_amount)}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>

                    <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/admin/documents/sales-orders/${order.id}`)
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/admin/documents/sales-orders/${order.id}/edit`)
                        }}
                        sx={{ textTransform: 'none' }}
                        disabled={order.status === 'cancelled'}
                      >
                        Edit
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              )
            })}
          </Grid>

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  sx={{ borderRadius: 2 }}
                >
                  Previous
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                  <Typography variant="body2">
                    Page {page} of {data.total_pages}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  disabled={page === data.total_pages}
                  onClick={() => setPage(page + 1)}
                  sx={{ borderRadius: 2 }}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          )}
        </>
      ) : (
        <>
          {/* List View */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Order Number</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Order Date</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Expected Delivery</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Total Amount</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Invoice Status</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.sales_orders?.map((order) => {
                  const statusConfig = getStatusConfig(order.status)
                  return (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleCardClick(order.id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          {order.order_number}
                        </Typography>
                        {order.estimate && (
                          <Typography variant="caption" color="textSecondary">
                            From: {order.estimate.estimate_number}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.customer?.billing_name || order.customer?.name || order.customer?.contact_person || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(order.order_date), 'dd/MM/yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.expected_delivery_date
                            ? format(new Date(order.expected_delivery_date), 'dd/MM/yyyy')
                            : '-'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusConfig.icon}
                          label={statusConfig.label}
                          color={statusConfig.color}
                          size="small"
                          sx={{ borderRadius: 4 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(order.total_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {order.converted_to_invoice_id ? (
                          <Chip
                            label="Invoiced"
                            color="success"
                            size="small"
                            icon={<InvoiceIcon />}
                            sx={{ borderRadius: 4 }}
                          />
                        ) : (
                          <Chip
                            label="Not Invoiced"
                            variant="outlined"
                            size="small"
                            sx={{ borderRadius: 4 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/admin/documents/sales-orders/${order.id}`)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/admin/documents/sales-orders/${order.id}/edit`)}
                              disabled={order.status === 'cancelled'}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More Actions">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuClick(e, order)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination for List View */}
          {data && data.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={data.total_pages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: { borderRadius: 2, minWidth: 200 }
          }
        }}
      >
        <MenuItem onClick={() => {
          navigate(`/admin/documents/sales-orders/${selectedOrder?.id}`)
          handleMenuClose()
        }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          navigate(`/admin/documents/sales-orders/${selectedOrder?.id}/edit`)
          handleMenuClose()
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><DeliveryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Create Delivery Challan</ListItemText>
        </MenuItem>
        {!selectedOrder?.converted_to_invoice_id && (
          <MenuItem onClick={handleConvertToInvoice}>
            <ListItemIcon><InvoiceIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Convert to Invoice</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Invoice Conversion Dialog */}
      {selectedOrder && (
        <InvoiceConversionDialog
          open={invoiceDialogOpen}
          salesOrderId={selectedOrder.id}
          salesOrderNumber={selectedOrder.order_number}
          onClose={() => {
            setInvoiceDialogOpen(false)
            setSelectedOrder(null)
          }}
          onSuccess={handleInvoiceConversionSuccess}
        />
      )}

      {/* Floating Action Button */}
      {hasPermission('orders.create') && (
        <Fab
          color="secondary"
          aria-label="add"
          onClick={() => navigate('/admin/documents/sales-orders/create')}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  )
}

export default SalesOrdersPage
